// @filename: client.ts
import { createTRPCProxyClient, httpLink } from '@trpc/client'
import { CreateTRPCReact, createTRPCReact } from '@trpc/react-query'
import { inferRouterOutputs } from '@trpc/server'
import { AppRouter } from '@vae/server'
import {
	addToQueue,
	getIsRefreshing,
	processQueue,
	setIsRefreshing,
} from '../authentication/authQueue'
import {
	clearTokens,
	getCustomAccessToken,
	refreshCustomAccessToken,
} from '../authentication/utilities'

const baseUrl = process.env.react_app_rest_url || process.env.REACT_APP_REST_URL

// 1. Initialize the React Client
export const trpc: CreateTRPCReact<AppRouter, unknown, unknown> = createTRPCReact<AppRouter>()

export const trpcClient = httpLink({
	url: `${baseUrl}trpc`,
	async headers() {
		const token = await getCustomAccessToken()

		if (!token) return {} // No token, proceed without Authorization header

		return { Authorization: `Bearer ${token}` }
	},
	fetch: async (input, init) => {
		try {
			const response = await fetch(input, init)
			const requestUrl = input.toString()

			const isRefreshRequest = requestUrl.includes('auth.refresh')
			const isLoginRequest = requestUrl.includes('auth.login')

			// Intercept 401s globally
			if (response.status === 401 && !isRefreshRequest && !isLoginRequest) {
				if (getIsRefreshing()) {
					return addToQueue()
						.then(newToken => {
							return fetch(input, {
								...init,
								headers: {
									...init?.headers,
									Authorization: `Bearer ${newToken}`,
								},
							})
						})
						.catch(err => Promise.reject(err))
				}

				setIsRefreshing(true)

				try {
					const result = await refreshCustomAccessToken()

					if (!result.ok) {
						processQueue(new Error('Token refresh failed'), null)

						if (result.isAuthFailure) {
							await clearTokens()
							window.location.href = '/'
						}

						return response // Return original 401
					}

					// Success! Process the queue with the new token
					processQueue(null, result.accessToken)

					// Retry the original request
					return fetch(input, {
						...init,
						headers: {
							...init?.headers,
							Authorization: `Bearer ${result.accessToken}`,
						},
					})
				} finally {
					setIsRefreshing(false)
				}
			}
			return response
		} catch (error) {
			return Promise.reject(error)
		}
	},
})

export type RouterOutputs = inferRouterOutputs<AppRouter>

// 3. Initialize the Vanilla/Background Client with the shared link
export const trpcProxyClient = createTRPCProxyClient<AppRouter>({
	links: [trpcClient],
})
